import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  CheckboxField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

const FormSchema = z.object({
  strategy_id: z.coerce.number(),
  portfolio_id: z.coerce.number().optional().nullable(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  instrument_type: z.string().optional().nullable(),
  unit_price: z.coerce.number().optional().nullable(),
  cost_price: z.coerce.number().optional().nullable(),
  accrued_interest: z.coerce.number().optional().nullable(),
  percentage_price: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  premium: z.coerce.number().optional().nullable(),
  discount: z.coerce.number().optional().nullable(),
  duration: z.coerce.number().optional().nullable(),
  sensitivity: z.coerce.number().optional().nullable(),
  credit_quality_step: z.coerce.number().int().optional().nullable(),
});

const client = "g4";
const table_name = "g4_dynamic_data";

export function G4DynamicDataNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const { t } = useTranslation();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("g4_dynamic_data.new_entry", { defaultValue: "New entry" })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="strategy_id"
                    label={t("g4_dynamic_data.columns.strategy_id", {
                      ns: "tables",
                      defaultValue: "Strategy ID",
                    })}
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    type="number"
                    name="portfolio_id"
                    label={t("g4_dynamic_data.columns.portfolio_id", {
                      ns: "tables",
                      defaultValue: "Portfolio ID",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("g4_dynamic_data.columns.date", {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="instrument_type"
                    label={t("g4_dynamic_data.columns.instrument_type", {
                      ns: "tables",
                      defaultValue: "Instrument type",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="unit_price"
                    label={t("g4_dynamic_data.columns.unit_price", {
                      ns: "tables",
                      defaultValue: "Unit price",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="cost_price"
                    label={t("g4_dynamic_data.columns.cost_price", {
                      ns: "tables",
                      defaultValue: "Cost price",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="accrued_interest"
                    label={t("g4_dynamic_data.columns.accrued_interest", {
                      ns: "tables",
                      defaultValue: "Accrued interest",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="percentage_price"
                    label={t("g4_dynamic_data.columns.percentage_price", {
                      ns: "tables",
                      defaultValue: "Percentage price",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="premium"
                    label={t("g4_dynamic_data.columns.premium", {
                      ns: "tables",
                      defaultValue: "Premium",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="discount"
                    label={t("g4_dynamic_data.columns.discount", {
                      ns: "tables",
                      defaultValue: "Discount",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="duration"
                    label={t("g4_dynamic_data.columns.duration", {
                      ns: "tables",
                      defaultValue: "Duration",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="sensitivity"
                    label={t("g4_dynamic_data.columns.sensitivity", {
                      ns: "tables",
                      defaultValue: "Sensitivity",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="credit_quality_step"
                    allowDecimal={false}
                    label={t("g4_dynamic_data.columns.credit_quality_step", {
                      ns: "tables",
                      defaultValue: "Cqs",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
